import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Toaster } from "sonner";
import { Send, ArrowRight } from "lucide-react";
import { CareersPerks } from "@/components/careers/CareersPerks";
import { PositionsGrid } from "@/components/careers/PositionsGrid";
import { HiringProcess } from "@/components/careers/HiringProcess";
import { ApplicationForm } from "@/components/careers/ApplicationForm";

const stagger = { animate: { transition: { staggerChildren: 0.06 } } };
const rise = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0, transition: { duration: 0.55 } } };

export default function Careers() {
  const [selectedKey, setSelectedKey] = useState(null);

  const applyTo = (positionKey) => {
    setSelectedKey(positionKey);
    setTimeout(() => {
      document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <div data-testid="careers-page" className="min-h-screen bg-brand-bg">
      <Toaster position="top-right" richColors />

      {/* HERO */}
      <section className="pt-24 pb-16 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial="initial" animate="animate" variants={stagger} className="max-w-3xl">
            <motion.div variants={rise} className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">
              Careers · Now Hiring
            </motion.div>
            <motion.h1 variants={rise} className="font-heading text-4xl md:text-6xl font-black tracking-tighter text-slate-900">
              Drive with us.<br />
              <span className="text-primary-brand">Grow with us.</span>
            </motion.h1>
            <motion.p variants={rise} className="mt-6 text-slate-700 leading-relaxed">
              We're building a professional trucking and logistics team out of Salt Lake City.
              If you take pride in what you do — behind the wheel, at the dispatch desk, or on the shop floor —
              there's a spot for you at Laronta Pro Logistic LLC.
            </motion.p>
            <motion.div variants={rise} className="mt-8 flex flex-wrap gap-3">
              <a
                href="#positions"
                data-testid="careers-hero-positions"
                className="inline-flex items-center gap-2 bg-primary-brand hover:bg-[#E04F00] text-white px-7 py-4 font-bold uppercase tracking-wider text-sm"
              >
                See Open Positions <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#apply-form"
                data-testid="careers-hero-apply"
                className="inline-flex items-center gap-2 border border-slate-300 hover:border-primary-brand hover:text-primary-brand text-slate-900 px-7 py-4 font-bold uppercase tracking-wider text-sm"
              >
                Apply Now <Send className="h-4 w-4" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <CareersPerks />
      <PositionsGrid onApply={applyTo} />
      <HiringProcess />

      {/* APPLICATION FORM */}
      <section id="apply-form" className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">Application Form</div>
          <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter text-slate-900">
            Tell us about you.
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Every submission is reviewed by our Operations Director. We reply to every applicant.
          </p>

          <ApplicationForm presetPosition={selectedKey} />
          <div className="mt-10 border border-slate-200 bg-white p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="text-sm text-slate-600">
              Have a client freight need instead of a career?
            </div>
            <Link
              to="/quote"
              className="inline-flex items-center gap-2 border border-primary-brand text-primary-brand hover:bg-primary-brand hover:text-white px-6 py-3 font-bold uppercase tracking-wider text-xs"
            >
              Request a Quote <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
